/**
 * Formats an ISO date-time string into a short, human-readable Belgian Dutch
 * format: "D MMM, HH:MM" (e.g. "7 mrt, 14:05").
 */
export function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('nl-BE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formats an ISO date-time string into a full Belgian Dutch date+time string:
 * "D MMM YYYY, HH:MM" (e.g. "7 mrt 2025, 14:05"). Returns empty string for
 * null/undefined input.
 */
export function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('nl-BE', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Formats an ISO date-time string into a date-splitter label in Belgian Dutch.
 * Returns "Vandaag · Vrijdag 9 mei" for today, or "Vrijdag 9 mei" for other days.
 */
export function formatDateSplitter(iso: string): string {
  const d = new Date(iso);
  const isToday = d.toDateString() === new Date().toDateString();
  const label = d.toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'short' });
  const capitalized = label.charAt(0).toUpperCase() + label.slice(1);
  return isToday ? `Vandaag · ${capitalized}` : capitalized;
}

/**
 * Date a conversation flagged inactive (conversations.flagged_inactive_at) will auto-close
 * if no new message arrives before then — mirrors the 14-day window in
 * apps/api/src/jobs/conversationLifecycle.ts. Formatted "DD/MM" (e.g. "30/08").
 */
export function inactiveClosesOnLabel(flaggedInactiveAt: string): string {
  const closesOn = new Date(new Date(flaggedInactiveAt).getTime() + 14 * 24 * 60 * 60 * 1000);
  return closesOn.toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit' });
}

/**
 * Formats an ISO date-time string into a full Belgian Dutch date+time string
 * with seconds precision (e.g. "7 mrt 2025, 14:05:32"). Returns "—" for null —
 * the operator tables' placeholder for "never happened" (e.g. no login yet).
 */
export function formatDateTimeFull(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'medium' });
}

/**
 * Countdown, in compact "Xd Xu"/"Xu"/"Xm" form, until a closed conversation is
 * soft-deleted (14 days after closing — mirrors SOFT_DELETE_AFTER in
 * apps/api/src/jobs/conversationLifecycle.ts). `closedAt` is conversations.updated_at.
 * The soft-delete itself only runs once a night, so a conversation can sit past its
 * deadline for up to ~24h waiting on that job — surfaced here as "enkele uren" rather
 * than a misleading precise-looking "1m" that never advances once the deadline passes.
 * Fits into the calling `wordt gewist binnen ${label}` template.
 */
export function deletionCountdownLabel(closedAt: string): string {
  const deletesAt = new Date(closedAt).getTime() + 14 * 24 * 60 * 60 * 1000;
  const diffMs = deletesAt - Date.now();
  if (diffMs <= 0) return 'enkele uren';
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days > 0) return `${days}d ${hours}u`;
  if (hours > 0) return `${hours}u`;
  const minutes = Math.max(1, Math.floor(diffMs / (1000 * 60)));
  return `${minutes}m`;
}
